import type { FragmentType } from 'src/gql';
import { useState } from 'react';
import { ToggleGroup, ToggleGroupItem } from '@savvy-kit/components/toggle-group';
import { TaxHistoryGraph } from 'src/components/advisor-dashboard/taxes/tax-history/tax-history-graph';
import { TaxHistoryTable } from 'src/components/advisor-dashboard/taxes/tax-history/tax-history-table';
import { graphql, unmaskFragment } from 'src/gql';

export const HouseholdTaxHistoryFragment = graphql(/* GraphQL */ `
  fragment HouseholdTaxHistory on TaxPageClient {
    userFullName
    taxTranscripts {
      id
    }
    ...TaxHistoryTableFragment
    ...TaxHistoryGraphFragment
  }
`);

type Props = {
  readonly primaryClient: FragmentType<typeof HouseholdTaxHistoryFragment> | undefined;
  readonly coClient?: FragmentType<typeof HouseholdTaxHistoryFragment> | null | undefined;
};

export function TaxHistory({ primaryClient: primaryReq, coClient: coReq }: Props) {
  const [activeClient, setActiveClient] = useState<'primary' | 'coClient'>('primary');

  const primaryClient = unmaskFragment(HouseholdTaxHistoryFragment, primaryReq);
  const coClient = unmaskFragment(HouseholdTaxHistoryFragment, coReq);

  const hasPrimaryData = !!primaryClient && (primaryClient.taxTranscripts?.length ?? 0) > 0;
  const hasCoClientData = !!coClient && (coClient.taxTranscripts?.length ?? 0) > 0;
  const hasAnyTaxData = hasPrimaryData || hasCoClientData;
  const hasCoClient = !!coClient;

  const activeClientData = hasCoClient && activeClient === 'coClient' ? coClient : primaryClient;

  const emptyDataState = (
    <p className="text-base text-secondary">Sign up for Savvy Tax to start recording tax history.</p>
  );

  return (
    <div className="flex flex-col gap-4">
      {!hasAnyTaxData ? (
        emptyDataState
      ) : (
        <>
          {hasCoClient && (
            <ToggleGroup
              value={[activeClient]}
              onValueChange={([value]) => {
                if (value && (value === 'primary' || value === 'coClient')) {
                  setActiveClient(value);
                }
              }}
              className="border border-slate-200 dark:border-border p-0 bg-slate-100 dark:bg-muted rounded-sm w-fit"
            >
              <ToggleGroupItem
                value="primary"
                className="flex-none px-4 py-2 text-sm font-medium data-pressed:bg-white dark:data-pressed:bg-card data-pressed:shadow-sm !rounded-l-sm"
              >
                {primaryClient?.userFullName || 'Primary client'}
              </ToggleGroupItem>
              <ToggleGroupItem
                value="coClient"
                className="flex-none px-4 py-2 text-sm font-medium data-pressed:bg-white dark:data-pressed:bg-card data-pressed:shadow-sm !rounded-r-sm"
              >
                {coClient.userFullName || 'Co client'}
              </ToggleGroupItem>
            </ToggleGroup>
          )}

          {(activeClientData?.taxTranscripts?.length ?? 0) > 0 ? (
            <div className="flex flex-col gap-8 mt-4">
              <TaxHistoryGraph clientData={activeClientData} />
              <TaxHistoryTable clientData={activeClientData} />
            </div>
          ) : (
            emptyDataState
          )}
        </>
      )}
    </div>
  );
}
